import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  InputNumber,
  Input,
  Typography,
  Descriptions,
  Divider,
  Space,
  Tag,
} from "antd";
import { PercentageOutlined } from "@ant-design/icons";
import { formatCurrencyWithDecimals as formatCurrency } from "../../../dependencies/custom/financeService";
import { formatDiscountPercentage } from "./discountPercentage";

const { Text, Title } = Typography;
const { TextArea } = Input;

/**
 * Modal for applying/editing discount on fee assignments
 * Works with School Fees, Custom Fees, Transport Fees, and BOT Clearance
 */
const ApplyDiscountModal = ({
  visible,
  onClose,
  onApply,
  feeType, // 'school' | 'transport' | 'bot'
  assignment,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const [previewAmount, setPreviewAmount] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);

  // Get the original amount based on fee type
  const getOriginalAmount = () => {
    if (!assignment) return 0;

    // If already has discount, use original_amount
    if (feeType === "bot") {
      return parseFloat(
        assignment.original_amount || assignment.total_amount || 0,
      );
    }
    return parseFloat(assignment.originalAmount || assignment.amountDue || 0);
  };

  // Get current discount percentage
  const getCurrentDiscount = () => {
    if (!assignment) return 0;
    if (feeType === "bot") {
      return parseFloat(assignment.discount_percentage || 0);
    }
    return parseFloat(assignment.discountPercentage || 0);
  };

  // Get current discount reason
  const getCurrentReason = () => {
    if (!assignment) return "";
    if (feeType === "bot") {
      return assignment.discount_reason || "";
    }
    return assignment.discountReason || "";
  };

  // Reset form when modal opens
  useEffect(() => {
    if (visible && assignment) {
      const currentDiscount = getCurrentDiscount();
      const currentReason = getCurrentReason();
      form.setFieldsValue({
        discountPercentage: currentDiscount,
        reason: currentReason,
      });
      calculatePreview(currentDiscount);
    }
  }, [visible, assignment]);

  // Calculate preview amounts
  const calculatePreview = (percentage) => {
    const original = getOriginalAmount();
    const discount = (original * (percentage || 0)) / 100;
    const finalAmount = original - discount;
    setDiscountAmount(discount);
    setPreviewAmount(finalAmount);
  };

  // Handle percentage change
  const handlePercentageChange = (value) => {
    calculatePreview(value);
  };

  // Handle form submit
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await onApply(assignment, values.discountPercentage, values.reason || "");
    } catch (error) {
      console.error("Form validation failed:", error);
    }
  };

  // Get fee type label
  const getFeeTypeLabel = () => {
    switch (feeType) {
      case "school":
        return "School Fee";
      case "transport":
        return "Transport Fee";
      case "bot":
        return "BOT Clearance";
      default:
        return "Fee";
    }
  };

  // Get fee name for display
  const getFeeName = () => {
    if (!assignment) return "";
    switch (feeType) {
      case "school":
        return (
          assignment.feeStructureName || assignment.feeTypeName || "School Fee"
        );
      case "transport":
        return `${assignment.routeName || "Transport"} - ${assignment.transportTypeName || ""}`;
      case "bot":
        return `BOT Clearance - ${assignment.botpc || ""}`;
      default:
        return "Fee";
    }
  };

  const isEditing = getCurrentDiscount() > 0;

  return (
    <Modal
      title={
        <Space>
          <PercentageOutlined />
          {isEditing ? "Edit Discount" : "Apply Discount"}
        </Space>
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={onClose}
      confirmLoading={loading}
      okText={isEditing ? "Update Discount" : "Apply Discount"}
      width={500}
    >
      {/* Fee Information */}
      <Descriptions
        column={1}
        size="small"
        bordered
        style={{ marginBottom: 16 }}
      >
        <Descriptions.Item label="Fee Type">
          <Tag color="blue">{getFeeTypeLabel()}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Fee Name">{getFeeName()}</Descriptions.Item>
        <Descriptions.Item label="Original Amount">
          <Text strong>{formatCurrency(getOriginalAmount())}</Text>
        </Descriptions.Item>
        {isEditing && (
          <Descriptions.Item label="Current Discount">
            <Tag color="orange">
              {formatDiscountPercentage(getCurrentDiscount())}%
            </Tag>
          </Descriptions.Item>
        )}
      </Descriptions>

      <Divider />

      {/* Discount Form */}
      <Form form={form} layout="vertical">
        <Form.Item
          name="discountPercentage"
          label="Discount Percentage"
          rules={[
            { required: true, message: "Please enter discount percentage" },
            {
              type: "number",
              min: 0,
              max: 100,
              message: "Percentage must be between 0 and 100",
            },
          ]}
        >
          <InputNumber
            min={0}
            max={100}
            step={0.5}
            precision={8}
            formatter={(value, info) =>
              info.userTyping ? info.input : formatDiscountPercentage(value)
            }
            style={{ width: "100%" }}
            addonAfter="%"
            placeholder="Enter discount percentage (0-100)"
            onChange={handlePercentageChange}
          />
        </Form.Item>

        <Form.Item name="reason" label="Reason for Discount">
          <TextArea
            rows={3}
            placeholder="Enter reason for discount (optional)"
            maxLength={255}
            showCount
          />
        </Form.Item>
      </Form>

      <Divider />

      {/* Preview Section */}
      <div style={{ background: "#f5f5f5", padding: 16, borderRadius: 8 }}>
        <Title level={5} style={{ marginBottom: 12 }}>
          Preview
        </Title>
        <Space direction="vertical" style={{ width: "100%" }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text>Original Amount:</Text>
            <Text>{formatCurrency(getOriginalAmount())}</Text>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text type="danger">Discount Amount:</Text>
            <Text type="danger">- {formatCurrency(discountAmount)}</Text>
          </div>
          <Divider style={{ margin: "8px 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text strong>Final Amount Due:</Text>
            <Text strong style={{ fontSize: 18, color: "#52c41a" }}>
              {formatCurrency(previewAmount)}
            </Text>
          </div>
        </Space>
      </div>
    </Modal>
  );
};

export default ApplyDiscountModal;
